import { BaseSyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { FormState, UseFormRegister } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import I18n from 'i18n-js';

import { register as registerAction } from '@lootboy/lib/actions/AuthActions';
import {
  ERegisterWithEmailFormField,
  TRegisterWithEmailFieldValues,
  useRegisterWithEmailForm,
} from '@lootboy/lib/forms/useRegisterWithEmailForm';
import { NOOP } from '@lootboy/lib/utils';

import Alert from '~/components/Alert';

import { useAppDispatch } from '~/redux/store/useAppDispatch';
import { REGISTER_TERMS_PATH } from '~/routes/paths';
import { parseLootBoyError } from '~/utilities/errors';

type TEmailRegistrationState = {
  captcha: {
    isRequired: boolean;
    dismiss: () => void;
    onChange: (key: string) => Promise<void>;
  };
  form: {
    register: UseFormRegister<TRegisterWithEmailFieldValues>;
    onSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
    errors: FormState<TRegisterWithEmailFieldValues>['errors'];
    isSubmitEnabled: boolean;
    values: {
      password: TRegisterWithEmailFieldValues[ERegisterWithEmailFormField.PASSWORD];
      submittedEmail?: TRegisterWithEmailFieldValues[ERegisterWithEmailFormField.EMAIL];
    };
  };
};

export const ACCOUNT_EXISTS_ERROR =
  'Api_AuthController_error_register_email_exists';

export function useEmailRegistrationView(): TEmailRegistrationState {
  const alert = useAlert();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [registerFormValues, setRegisterFormValues] = useState<
    | Omit<
        TRegisterWithEmailFieldValues,
        ERegisterWithEmailFormField.REPEAT_PASSWORD
      >
    | undefined
  >();

  const {
    register,
    onFormSubmit,
    isSubmitEnabled,
    setError,
    watch,
    trigger,
    formState: { errors },
  } = useRegisterWithEmailForm((formValues) => {
    setShowCaptcha(true);
    setRegisterFormValues(formValues);
  });

  const password = watch(ERegisterWithEmailFormField.PASSWORD);
  const repeatPassword = watch(ERegisterWithEmailFormField.REPEAT_PASSWORD);

  useEffect(() => {
    if (password && repeatPassword) {
      trigger(ERegisterWithEmailFormField.REPEAT_PASSWORD).catch(NOOP);
    }
  }, [password, trigger, repeatPassword]);

  const dismissCaptcha = useCallback(() => {
    setShowCaptcha(false);
    setRegisterFormValues(undefined);
  }, []);

  const onCaptchaChange = useCallback(
    async (key: string) => {
      if (!registerFormValues) {
        return;
      }
      try {
        await dispatch(
          registerAction(
            registerFormValues.email,
            registerFormValues.password,
            key,
          ),
        );
        history.push(REGISTER_TERMS_PATH);
      } catch (error) {
        const { message, messageId } = parseLootBoyError(error);
        setShowCaptcha(false);
        if (messageId === ACCOUNT_EXISTS_ERROR) {
          setError(ERegisterWithEmailFormField.EMAIL, { message: messageId });
          return;
        }
        alert.error(
          <Alert
            title={I18n.t('common_error')}
            body={message}
          />,
        );
      }
    },
    [dispatch, registerFormValues, setError, alert, history],
  );

  return {
    captcha: {
      isRequired: showCaptcha,
      dismiss: dismissCaptcha,
      onChange: onCaptchaChange,
    },
    form: {
      register,
      errors,
      isSubmitEnabled,
      onSubmit: onFormSubmit,
      values: { password, submittedEmail: registerFormValues?.email },
    },
  };
}
